import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Wifi, Cpu, Phone, Cable, MapPin, Mail, Hash, Activity, Clock, AlertTriangle, Download, Wrench } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';

interface ActivityItem {
  at: string;
  kind: string;
  severity: 'info' | 'warning' | 'critical' | 'major' | 'minor' | string;
  source: string;
  target_id: number;
  target_kind: 'device' | 'element' | string;
  code: string | null;
  details: Record<string, unknown> | null;
}

interface CustomerDetailData {
  id: number; name: string; external_id: string | null;
  email: string | null; phone: string | null;
  address_line1: string | null; city: string | null; state: string | null; postal_code: string | null;
  status: string;
  service_plan?: { id: number; name: string } | null;
  devices: Array<{ id: number; serial_number: string | null; mac_address: string | null; is_online: boolean; last_inform_at: string | null; model?: { manufacturer: string; model_name: string } | null }>;
  onts: Array<{ id: number; vendor_local_id: string; vendor_serial: string | null; model: string | null; oper_status: string; admin_status: string; last_seen_at: string | null; external_id: string | null; olt?: { id: number; name: string; vendor: string } | null; parent?: { vendor_local_id: string } | null }>;
  sub_counts: Record<string, number>;
  voice_counts: Record<string, number>;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [activityDays, setActivityDays] = useState(14);
  const { data, isLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<{ data: CustomerDetailData }>(`/customers/${id}`).then((r: { data: { data: CustomerDetailData } }) => r.data.data),
    enabled: !!id,
  });
  const { data: activity } = useQuery({
    queryKey: ['customer-activity', id, activityDays],
    queryFn: () => api.get<{ data: ActivityItem[]; meta: { since: string; count: number } }>(`/customers/${id}/activity`, { params: { days: activityDays } }).then((r: { data: { data: ActivityItem[]; meta: { since: string; count: number } } }) => r.data),
    enabled: !!id,
  });

  if (isLoading) return <div className="p-8 text-text-muted text-sm">Chargement…</div>;
  if (!data) return <div className="p-8 text-text-faint italic">Customer introuvable.</div>;

  const statusColor = data.status === 'active' ? 'text-success' : data.status === 'suspended' ? 'text-danger' : 'text-warning';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title={data.name}
        subtitle={<span className={statusColor}>{data.status}</span>}
        actions={
          <Link to="/app/customers" className="btn btn-sm">← Customers</Link>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Identité */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Activity size={13} />Identité</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <Field icon={<Hash size={11} />} label="External ID" value={data.external_id ?? '—'} mono />
            <Field icon={<Mail size={11} />} label="Email" value={data.email ?? '—'} />
            <Field icon={<Phone size={11} />} label="Phone" value={data.phone ?? '—'} mono />
            <Field icon={<MapPin size={11} />} label="Adresse" value={[data.address_line1, data.city, data.state, data.postal_code].filter(Boolean).join(', ') || '—'} />
            {data.service_plan && (
              <Field icon={<Cable size={11} />} label="Plan" value={data.service_plan.name} />
            )}
          </div>
        </div>

        {/* ONTs */}
        <div className="card">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Wifi size={13} />ONTs ({data.onts.length})</h3>
            <div className="text-[11px] text-text-faint">
              {Object.entries(data.sub_counts).map(([s, c]) => <span key={s} className="ml-2">data <b>{Number(c)}</b> {s}</span>)}
              {Object.entries(data.voice_counts).map(([s, c]) => <span key={s} className="ml-2">voice <b>{Number(c)}</b> {s}</span>)}
            </div>
          </div>
          {data.onts.length === 0 ? (
            <div className="p-6 text-center text-text-faint italic text-xs">Aucun ONT linké à ce customer.</div>
          ) : (
            <table className="luc-table">
              <thead>
                <tr>
                  <th>Status</th><th>OLT / PON</th><th>Seat</th><th>Serial</th><th>Model</th><th>Last seen</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.onts.map((o: CustomerDetailData['onts'][number]) => (
                  <tr key={o.id}>
                    <td>
                      <span className={`dot ${o.oper_status === 'up' ? 'dot-up' : o.oper_status === 'down' ? 'dot-down' : 'dot-warn'}`} />
                      <span className="ml-1.5 text-xs">{o.oper_status}</span>
                    </td>
                    <td className="font-mono text-[11.5px] text-text-muted">{o.olt?.name} <span className="text-text-faint">·</span> {o.parent?.vendor_local_id}</td>
                    <td className="font-mono text-[11px]">{o.vendor_local_id}</td>
                    <td className="font-mono text-[11.5px]">{o.vendor_serial ?? '—'}</td>
                    <td className="text-[11.5px] text-text-muted">{o.model ?? '—'}</td>
                    <td className="font-mono text-[11px] text-text-faint">{o.last_seen_at ? new Date(o.last_seen_at).toLocaleString('fr') : '—'}</td>
                    <td>
                      <Link to={`/app/onts/${o.id}`} className="btn btn-sm">→ ONT</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Activity timeline */}
        <div className="card">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Clock size={13} />Activité ({activity?.meta.count ?? 0} events)</h3>
            <div className="flex items-center gap-1 bg-bg-2 border border-border rounded text-xs">
              {[1, 7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setActivityDays(d)}
                  className={`h-6 px-2 ${activityDays === d ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}
                >
                  {d}j
                </button>
              ))}
            </div>
          </div>
          {!activity?.data.length ? (
            <div className="p-6 text-center text-text-faint italic text-xs">Aucune activité sur cette période.</div>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="luc-table text-[11px]">
                <thead className="sticky top-0 bg-bg z-10">
                  <tr><th>Heure</th><th>Kind</th><th>Source</th><th>Target</th><th>Détails</th></tr>
                </thead>
                <tbody>
                  {activity.data.map((e: ActivityItem, i: number) => {
                    const icon = e.kind.startsWith('alarm') ? <AlertTriangle size={10} /> : e.kind.startsWith('firmware') ? <Download size={10} /> : e.kind.startsWith('olt') ? <Wrench size={10} /> : <Activity size={10} />;
                    const color = e.severity === 'critical' || e.severity === 'major' ? 'text-danger' : e.severity === 'warning' || e.severity === 'minor' ? 'text-warning' : 'text-text-muted';
                    return (
                      <tr key={i}>
                        <td className="font-mono text-text-faint whitespace-nowrap">{new Date(e.at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                        <td><span className={`flex items-center gap-1 ${color}`}>{icon}<span className="font-mono">{e.kind}</span></span></td>
                        <td className="text-text-faint">{e.source}</td>
                        <td className="font-mono text-text-muted">
                          {e.target_kind === 'device' ? <Link to={`/app/devices/${e.target_id}`} className="hover:text-brand">dev#{e.target_id}</Link> : `el#${e.target_id}`}
                        </td>
                        <td className="text-text-muted truncate max-w-md" title={JSON.stringify(e.details)}>
                          {e.code && <span className="font-mono mr-1">{e.code}</span>}
                          {e.details ? <span className="text-[10px] text-text-faint">{JSON.stringify(e.details).slice(0, 120)}</span> : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CPE Devices (TR-069) */}
        <div className="card">
          <div className="px-4 py-2.5 border-b border-border">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Cpu size={13} />CPE Devices ({data.devices.length})</h3>
          </div>
          {data.devices.length === 0 ? (
            <div className="p-6 text-center text-text-faint italic text-xs">Aucun CPE TR-069 lié.</div>
          ) : (
            <table className="luc-table">
              <thead>
                <tr><th>Online</th><th>Serial</th><th>MAC</th><th>Modèle</th><th>Last inform</th><th></th></tr>
              </thead>
              <tbody>
                {data.devices.map((d: CustomerDetailData['devices'][number]) => (
                  <tr key={d.id}>
                    <td>
                      <span className={`dot ${d.is_online ? 'dot-up' : 'dot-down'}`} />
                      <span className="ml-1.5 text-xs">{d.is_online ? 'online' : 'offline'}</span>
                    </td>
                    <td className="font-mono text-[11.5px]">{d.serial_number ?? '—'}</td>
                    <td className="font-mono text-[11px] text-text-muted">{d.mac_address ?? '—'}</td>
                    <td className="text-[11.5px] text-text-muted">{d.model ? `${d.model.manufacturer} ${d.model.model_name}` : '—'}</td>
                    <td className="font-mono text-[11px] text-text-faint">{d.last_inform_at ? new Date(d.last_inform_at).toLocaleString('fr') : '—'}</td>
                    <td>
                      <Link to={`/app/devices/${d.id}`} className="btn btn-sm">→ Device</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-text-faint font-medium">{icon}{label}</span>
      <span className={`${mono ? 'font-mono' : ''} text-xs text-text`}>{value}</span>
    </div>
  );
}
